// Who (step 2): Everyone or Specific people, then one search box over every
// kind the product can name (users, groups, OUs), each pick a chip with its
// kind's icon. A product with a single field, a custom picker or a one-only
// field (Apps) keeps its own field. Except sits right under it, behind
// "Except someone".
import React, { useState } from "react";
import { FolderTree, User, UserMinus, Users, X } from "lucide-react";

import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";
import { EXCEPT_HELP } from "../../../components/policyShell/signals";
import { ErrorText, Field, Group, Segmented, StepCard, TextButton } from "../ui";
import RemotePicker from "./RemotePicker";
import { fieldDomId } from "./draft";

const depsKeyOf = (field, draft) => JSON.stringify(field.deps ? field.deps(draft) : []);
const KIND_ICON = { users: User, groups: Users, ous: FolderTree };

/** Chips with their kind's icon, for "<kind>:<value>" picker values. */
export function kindTagRender({ value, closable, onClose }) {
  const v = String(value);
  const i = v.indexOf(":");
  const kind = i > 0 ? v.slice(0, i) : "";
  const name = i > 0 ? v.slice(i + 1) : v;
  const Icon = KIND_ICON[kind] || User;
  return (
    <span
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.3125rem",
        margin: "0.125rem 0.25rem 0.125rem 0",
        padding: "0.0625rem 0.375rem 0.0625rem 0.5rem",
        borderRadius: "1rem",
        background: AN_COLORS.primaryL,
        border: `1px solid ${AN_COLORS.line}`,
        color: AN_COLORS.ink,
        fontSize: "0.8125rem",
        fontFamily: AN_FONT,
        lineHeight: 1.5,
        maxWidth: "100%",
      }}
    >
      <Icon size={12} aria-hidden style={{ color: AN_COLORS.primary, flexShrink: 0 }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
      {closable && (
        <button
          type="button"
          aria-label={`Remove ${name}`}
          onClick={onClose}
          style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", color: AN_COLORS.muted, display: "inline-flex" }}
        >
          <X size={12} />
        </button>
      )}
    </span>
  );
}

// One combined picker only when every field is a plain multi-pick search.
const combinable = (fields) => fields.length > 1 && fields.every((f) => !f.Picker && !f.single && f.search);

const nounOf = (f) => (f.kind === "ous" ? "OUs" : f.label.toLowerCase());

export default function WhoSection({ adapter, draft, patchDraft, errors, touch, data }) {
  const who = draft.who || {};
  const cfg = adapter.who || {};
  const fields = cfg.fields || [];
  const everyone = !!(cfg.everyone && who.everyone);
  const ctx = { draft, data };
  const setList = (key, v) => patchDraft({ who: { ...who, [key]: Array.isArray(v) ? v : v ? [v] : [] } });
  const peopleError = errors["who.people"];
  const picked = fields.some((f) => (who[f.key] || []).length);
  const exceptCount = (draft.except?.users || []).length + (draft.except?.groups || []).length;
  const [exceptOpen, setExceptOpen] = useState(exceptCount > 0);
  const canExcept = exceptFieldsOf(adapter).length > 0;

  let pickers;
  if (combinable(fields)) {
    const value = fields.flatMap((f) => (who[f.key] || []).map((v) => `${f.key}:${v}`));
    const reason = fields.map((f) => f.disabledReason && f.disabledReason(draft, data)).find(Boolean);
    const onChange = (vals) => {
      const next = { ...who };
      fields.forEach((f) => {
        next[f.key] = [];
      });
      vals.forEach((v) => {
        const i = v.indexOf(":");
        const k = v.slice(0, i);
        if (next[k]) next[k].push(v.slice(i + 1));
      });
      patchDraft({ who: next });
    };
    const nouns = fields.map(nounOf);
    pickers = (
      <Field htmlFor="pf-who-people">
        <RemotePicker
          id="pf-who-people"
          ariaLabel={`Search ${nouns.join(", ")}`}
          sources={fields.map((f) => ({ key: f.key, label: f.label, search: (q) => f.search(q, ctx) }))}
          prefixed
          depsKey={fields.map((f) => depsKeyOf(f, draft)).join("|")}
          value={value}
          onChange={onChange}
          onBlur={() => touch("who.people")}
          disabled={!!reason}
          placeholder={reason || `Search ${nouns.slice(0, -1).join(", ")} or ${nouns[nouns.length - 1]}`}
          status={peopleError ? "error" : undefined}
          tagRender={kindTagRender}
        />
      </Field>
    );
  } else {
    pickers = (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(13rem, 1fr))", gap: "1rem" }}>
        {fields.map((f) => {
          const reason = f.disabledReason ? f.disabledReason(draft, data) : null;
          const value = who[f.key] || [];
          return (
            <Field key={f.key} label={f.label} htmlFor={`pf-who-${f.key}`}>
              {f.Picker ? (
                <f.Picker value={value} onChange={(v) => setList(f.key, v)} draft={draft} data={data} />
              ) : (
                <RemotePicker
                  id={`pf-who-${f.key}`}
                  ariaLabel={f.label}
                  sources={[{ key: f.key, label: f.label, search: (q) => f.search(q, ctx) }]}
                  depsKey={depsKeyOf(f, draft)}
                  single={f.single}
                  value={f.single ? value[0] || "" : value}
                  onChange={(v) => setList(f.key, v)}
                  onBlur={() => touch("who.people")}
                  disabled={!!reason}
                  placeholder={reason || f.placeholder}
                  status={peopleError ? "error" : undefined}
                />
              )}
            </Field>
          );
        })}
      </div>
    );
  }

  return (
    <StepCard
      id="policy-section-who"
      step={2}
      title="Who does it apply to?"
      help={cfg.everyone ? "Everyone in scope, or only the people you pick." : "Pick who this policy is for."}
      done={everyone || picked}
      error={!!peopleError}
    >
      {cfg.everyone && (
        <Segmented
          ariaLabel="Who this policy applies to"
          value={everyone ? "everyone" : "specific"}
          onChange={(v) => patchDraft({ who: { ...who, everyone: v === "everyone" } })}
          options={[
            { value: "everyone", label: "Everyone", tone: "brand" },
            { value: "specific", label: "Specific people", tone: "brand" },
          ]}
        />
      )}

      {!everyone && (
        <div id={fieldDomId("who.people")} style={{ display: "grid", gap: "0.5rem", scrollMarginTop: "1rem" }}>
          {pickers}
          <ErrorText>{peopleError}</ErrorText>
        </div>
      )}

      {canExcept &&
        (exceptOpen ? (
          <ExceptField adapter={adapter} draft={draft} patchDraft={patchDraft} data={data} onClose={exceptCount ? null : () => setExceptOpen(false)} />
        ) : (
          <div>
            <TextButton onClick={() => setExceptOpen(true)} style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem" }}>
              <UserMinus size={14} aria-hidden />
              Except someone
            </TextButton>
          </div>
        ))}
    </StepCard>
  );
}

/** The fields Except searches: those with `except` ("users" | "groups"). */
export const exceptFieldsOf = (adapter) => (adapter.who?.except === false ? [] : (adapter.who?.fields || []).filter((f) => f.except));

// Except: users and groups this policy never applies to, written to
// conditions.identity.excludeUsers / excludeGroups.
export function ExceptField({ adapter, draft, patchDraft, data, onClose }) {
  const fields = exceptFieldsOf(adapter);
  if (!fields.length) return null;
  const ctx = { draft, data };
  const value = [...(draft.except?.users || []).map((u) => `users:${u}`), ...(draft.except?.groups || []).map((g) => `groups:${g}`)];
  const setExcept = (vals) => {
    const next = { users: [], groups: [] };
    vals.forEach((v) => {
      const i = v.indexOf(":");
      const kind = v.slice(0, i);
      if (next[kind]) next[kind].push(v.slice(i + 1));
    });
    patchDraft({ except: next });
  };
  const nouns = fields.map((f) => (f.except === "users" ? "users" : "groups"));
  const reason = fields.map((f) => f.disabledReason && f.disabledReason(draft, data)).find(Boolean);
  return (
    <div style={{ borderTop: `1px dashed ${AN_COLORS.line}`, paddingTop: "1rem" }}>
      <Group
        id="policy-advanced-except"
        title="Except"
        help={EXCEPT_HELP}
        aside={onClose && <TextButton onClick={onClose} style={{ color: AN_COLORS.muted }}>Hide</TextButton>}
      >
        <RemotePicker
          id="pf-except"
          ariaLabel="Except"
          sources={fields.map((f) => ({ key: f.except, label: f.label, search: (q) => f.search(q, ctx) }))}
          prefixed
          depsKey={fields.map((f) => depsKeyOf(f, draft)).join("|")}
          value={value}
          onChange={setExcept}
          placeholder={reason || `Search ${nouns.join(" or ")}`}
          disabled={!!reason}
          tagRender={kindTagRender}
        />
      </Group>
    </div>
  );
}
